import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRateLimitState } from '../services/rateLimit';
import BlockedPage from '../components/BlockedPage';
import { LoadingSpinner } from '../components/ui';
import { supabase } from '../lib/supabase';

export function withRateLimitCheck<P extends object>(
  WrappedComponent: React.ComponentType<P>
) {
  return function WithRateLimitCheck(props: P) {
    const [isBlocked, setIsBlocked] = useState(false);
    const [blockedUntil, setBlockedUntil] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState<ReturnType<typeof supabase.channel> | null>(null);
    const navigate = useNavigate();
    const { id: noteId } = useParams<{ id: string }>();

    useEffect(() => {
      let mounted = true;

      const setupSubscription = async () => {
        try {
          // Skip rate limiting for routes that don't require a note ID
          if (!noteId) {
            console.log('No note ID present, skipping rate limit check');
            setLoading(false);
            return;
          }

          // Check initial rate limit state
          console.log('Checking initial rate limit state');
          const initialState = await getRateLimitState(noteId);
          if (!mounted) return;
          
          console.log('Initial rate limit state:', initialState);
          
          if (initialState.isBlocked) {
            console.log('User is blocked for this note');
            setIsBlocked(true);
            setBlockedUntil(initialState.blockedUntil);
            setLoading(false);
            return;
          }

          const { data: { user_ip } } = await supabase.rpc('get_client_ip');
          
          if (!user_ip || !mounted) {
            console.warn('No user IP or component unmounted');
            setLoading(false);
            return;
          }

          console.log('Setting up rate limit subscription for IP:', user_ip);
          
          const subscribedEvent = 'postgres_changes';
          console.log(`Subscribing to ${subscribedEvent} event on rate_limits table for IP ${user_ip}`);

          const newSubscription = supabase
            .channel('rate_limits_check')
            .on(
              subscribedEvent,
              {
                event: '*',
                schema: 'public',
                table: 'rate_limits',
                filter: `ip=eq.${user_ip}`
              },
              async (payload) => {
                if (!mounted) {
                  console.log('Ignoring rate limit update - component unmounted');
                  return;
                }

                console.log('Received rate limit update - Full payload:', payload);
                
                try {
                  console.log('Fetching updated rate limit state');
                  const state = await getRateLimitState(noteId);
                  console.log('Updated rate limit state:', state);
                  
                  if (state.isBlocked) {
                    console.log('User is now blocked until:', state.blockedUntil);
                    setIsBlocked(true);
                    setBlockedUntil(state.blockedUntil);
                  } else {
                    console.log('User is not blocked');
                    setIsBlocked(false);
                    setBlockedUntil(null);
                  }
                } catch (error) {
                  console.error('Failed to update rate limit state:', error);
                }
              }
            )
            .subscribe((status, error) => {
              console.log(`Rate limit subscription status for ${subscribedEvent}:`, status);
              if (error) {
                console.error(`Rate limit subscription error for ${subscribedEvent}:`, error);
              }
            });

          console.log('Subscription setup complete');
          setSubscription(newSubscription);
          setLoading(false);
        } catch (error) {
          console.error('Failed to setup rate limit subscription:', error);
          setLoading(false);
        }
      };

      setupSubscription();

      return () => {
        mounted = false;
        if (subscription) {
          console.log('Cleaning up rate limit subscription');
          supabase.removeChannel(subscription);
        }
      };
    }, [noteId]);

    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (isBlocked && blockedUntil) {
      return <BlockedPage blockedUntil={blockedUntil} />;
    }

    return <WrappedComponent {...props} />;
  };
}